import { combineReducers } from '@reduxjs/toolkit';
import { omit, range } from 'lodash/fp';
import { createReducer } from 'typesafe-actions';
import { v4 as uuidv4 } from 'uuid';

import { TYPE_LINE_CHART } from '@portal/common/models/chart-type';
import {
    APPARENT_ID_KEY,
    INFECTIOUS_SYNDROME_ID_KEY,
    MEASURE_ID_KEY,
    SEVERITY_ID_KEY,
    SYMPTOMATIC_ID_KEY,
} from '@portal/common/models/data-key';
import { AllDataTypes, CauseOutcome } from '@portal/common/models/data-type';
import { OPERATION_STATUS, OperationStatus } from '@portal/common/models/operation-status';
import {
    AgeGroupEntity,
    ChartType,
    ConditionDetailNotesRecord,
    CountryLocationSdi,
    DataCollectionResource,
    DataCondition,
    DataFilters,
    DataGranularity,
    DataKey,
    DataResponse,
    DataTableDisplayState,
    DataType,
} from '@portal/common/types';

import { LimitModal } from '../../models/data-tool';
import sortRefinementsOptions from '../../utility/sort-refinements-options';
import {
    QueriedDataSettings,
    addVisibleRefinementFilters,
    applyRelevantPreservedChartFilters,
    calculateChartDataResponsesFromDataQuery,
    cancelSelectionCountModal,
    changeChartDimension,
    changeChartFilters,
    changeCombinedQueriedFiltersWithoutTimeUnitsCount,
    changeConditions,
    changeDataTypeSelection,
    changeDataTypeSelectionForSingleConditionTool,
    changeQueriedConditions,
    changeQueriedConditionsRefinedGranularity,
    changeQueriedDataSettings,
    changeQueriedRefinementFilters,
    changeSelectedChartType,
    changeSelectedDataType,
    changeSelectedRefinementFilters,
    clearConditionDetailNotes,
    clearDengueBaselineData,
    clearPdfReportSendStatus,
    clearSelectedConditionsRefinedGranularity,
    confirmSelectionCountModal,
    dataSelectionValidation,
    emulateDataIsLoadingToMakeChartsRefresh,
    fetchDengueBaselineDataAsync,
    fixMissingLoadedRefinementFilters,
    getAgeGroupsAsync,
    getConditionDetailNotes,
    getCountryLocationsSdi,
    getDataCollectionsAsync,
    getRefinedGranularityAsync,
    getSingleYearAgeGranularitiesAsync,
    initializeChartFilters,
    initializeDataTableDisplayState,
    initializeDataTypeSelection,
    initializeDefaultRefinementFilters,
    initializeIncludeNowcasts,
    initializeSelectedChartType,
    initializeSelectedDataType,
    initializeVisibleRefinementFilters,
    openSelectionCountModal,
    queryChartDataAsync,
    queryDataAsync,
    removeVisibleRefinementFilters,
    resetDataTool,
    resetFiltersChangedSinceLastRequest,
    resetPdfReportSend,
    sendPdfReportAsync,
    setCustomChartSettings,
    setDrillIntoCondition,
    setSamplePopulationSize,
    setYearRefinementFilterMode,
    toggleDayRefinementFilterMode,
    toggleDengueAvertedOutcome,
    toggleDengueCumulativeOutcome,
    toggleIncludeNowcasts,
    toggleYearRefinementFilterMode,
    updateDataTableDisplayState,
    validateChartFilters,
    validatePreservedChartFilters,
    validateVisibleRefinementFilters,
    validateYearRefinementFilters,
} from './actions';
import { filtersChangedSinceLastRequestActions } from './filters-changed-actions';

export type State = {
    initializationKey: string | null;
    ageGroups: null | AgeGroupEntity[];
    countryLocationSdi: null | CountryLocationSdi[];
    dataCollections: null | DataCollectionResource[];

    selectedDataType: DataType;
    selectedChartType: ChartType;
    selectedConditions: DataCondition[];
    selectedConditionsRefinedGranularity: null | DataGranularity;
    selectedRefinementFilters: DataFilters | null;

    queriedConditions: DataCondition[];
    queriedRefinementFilters: DataFilters | null;
    queriedConditionsRefinedGranularity: null | DataGranularity;
    queriedDataSettings: QueriedDataSettings | null;

    filtersChangedSinceLastRequest: boolean;
    visibleRefinementFilters: string[];
    yearRefinementFilterMode: 'slider' | 'dropdown';
    dayRefinementFilterMode: 'slider' | 'dropdown';
    includeNowcasts: boolean;

    isDataLoading: boolean;
    dataResponsesByType: null | Record<DataType, DataResponse>;

    dataTableDisplayState: DataTableDisplayState;

    combinedQueriedFiltersWithoutTimeUnitsCount: number;
    activeSelectionCountModal: LimitModal | null;

    singleYearAgeGranularity: null | DataGranularity;

    conditionDetailNotes: null | ConditionDetailNotesRecord[];

    isChartDataLoading: boolean;
    chartDataResponsesByType: null | Record<string, DataResponse>;
    preservedChartFilters: DataFilters | null;
    chartFilters: DataFilters | null;
    omittedChartFilters: DataKey[];
    customChartSettings: Record<string, any>;

    chartDimensionFilter: DataKey | null;
    drillIntoCondition: DataCondition | null;

    samplePopulationSize: number;

    dengueCalculations: {
        showAvertedOutcome: boolean;
        showCumulativeOutcome: boolean;
        baselineData: null | Record<string, DataResponse>;
        isLoadingBaseline: boolean;
    };

    // "Email this report" send lifecycle; failedAttempts is per-recipient (keyed by lastEmail).
    pdfReportSend: {
        status: OperationStatus;
        lastEmail: string;
        failedAttempts: number;
    };
};

const initialState: State = {
    initializationKey: null,

    ageGroups: null,
    countryLocationSdi: null,
    dataCollections: null,

    selectedDataType: AllDataTypes,
    selectedChartType: TYPE_LINE_CHART,
    selectedConditions: [],
    selectedConditionsRefinedGranularity: null,
    selectedRefinementFilters: null,
    queriedConditions: [],
    queriedRefinementFilters: null,
    queriedConditionsRefinedGranularity: null,
    queriedDataSettings: null,
    filtersChangedSinceLastRequest: true,
    visibleRefinementFilters: [
        APPARENT_ID_KEY,
        SYMPTOMATIC_ID_KEY,
        SEVERITY_ID_KEY,
        INFECTIOUS_SYNDROME_ID_KEY,
        MEASURE_ID_KEY,
    ],
    yearRefinementFilterMode: 'slider',
    dayRefinementFilterMode: 'slider',
    includeNowcasts: false,

    singleYearAgeGranularity: null,

    combinedQueriedFiltersWithoutTimeUnitsCount: 0,
    activeSelectionCountModal: null,

    dataTableDisplayState: {
        sortedDescending: true,
        sortedByColumn: null,
        visibleColumns: null,
    },

    isDataLoading: false,
    dataResponsesByType: null,

    conditionDetailNotes: null,

    isChartDataLoading: false,
    chartDataResponsesByType: null,
    chartFilters: null,
    preservedChartFilters: null,
    omittedChartFilters: [],
    customChartSettings: {},

    chartDimensionFilter: null,
    drillIntoCondition: {
        data_type: CauseOutcome,
        level: 2,
    },

    samplePopulationSize: 100000,

    dengueCalculations: {
        showAvertedOutcome: false,
        showCumulativeOutcome: false,
        baselineData: null,
        isLoadingBaseline: false,
    },

    pdfReportSend: {
        status: OPERATION_STATUS.IDLE,
        lastEmail: '',
        failedAttempts: 0,
    },
};

export default combineReducers<State>({
    initializationKey: createReducer(initialState.initializationKey).handleAction(
        resetDataTool,
        (state, action) => uuidv4()
    ),
    ageGroups: createReducer(initialState.ageGroups).handleAction(
        getAgeGroupsAsync.success,
        (state, action) => action.payload
    ),
    countryLocationSdi: createReducer(initialState.countryLocationSdi).handleAction(
        getCountryLocationsSdi.success,
        (state, action) => action.payload
    ),
    dataCollections: createReducer(initialState.dataCollections).handleAction(
        getDataCollectionsAsync.success,
        (state, action) => action.payload
    ),

    selectedDataType: createReducer(initialState.selectedDataType)
        .handleAction(
            resetDataTool,
            (state, action) => action.payload.defaultDataType || initialState.selectedDataType
        )
        .handleAction(
            [initializeSelectedDataType, changeSelectedDataType],
            (state, action) => action.payload
        ),
    selectedChartType: createReducer(initialState.selectedChartType)
        .handleAction([resetDataTool], (state, action) => initialState.selectedChartType)
        .handleAction(
            [initializeSelectedChartType, changeSelectedChartType],
            (state, action) => action.payload
        ),

    selectedConditions: createReducer(initialState.selectedConditions)
        .handleAction([resetDataTool], (state, _) => initialState.selectedConditions)
        .handleAction(changeConditions, (state, action) => action.payload)
        .handleAction(
            [
                initializeDataTypeSelection,
                changeDataTypeSelection,
                changeDataTypeSelectionForSingleConditionTool,
            ],
            (state, action) => action.payload.conditions
        ),
    selectedConditionsRefinedGranularity: createReducer(
        initialState.selectedConditionsRefinedGranularity
    )
        .handleAction(
            [resetDataTool, changeSelectedDataType, clearSelectedConditionsRefinedGranularity],
            (state, _) => initialState.selectedConditionsRefinedGranularity
        )
        .handleAction(getRefinedGranularityAsync.success, (state, action) => action.payload),
    selectedRefinementFilters: createReducer(initialState.selectedRefinementFilters)
        .handleAction([resetDataTool], (state, _) => initialState.selectedRefinementFilters)
        .handleAction(
            [
                initializeDataTypeSelection,
                changeDataTypeSelection,
                changeDataTypeSelectionForSingleConditionTool,
            ],
            (state, action) => action.payload.refinementFilters
        )
        .handleAction(
            [initializeDefaultRefinementFilters, fixMissingLoadedRefinementFilters],
            (state, action) => action.payload
        )
        .handleAction(
            [changeSelectedRefinementFilters, validateYearRefinementFilters],
            (state, action) => ({
                ...state,
                ...action.payload,
            })
        )
        .handleAction(toggleYearRefinementFilterMode, (state, action) => {
            const { year } = state;
            if (year == null) {
                return state;
            }

            const min = Math.min(...year);
            const max = Math.max(...year);
            const yearRange = range(min, max + 1);
            return {
                ...state,
                year: yearRange,
            };
        }),
    queriedConditions: createReducer(initialState.queriedConditions)
        .handleAction([resetDataTool], (state, _) => initialState.queriedConditions)
        .handleAction(changeQueriedConditions, (state, action) => action.payload),
    queriedRefinementFilters: createReducer(initialState.queriedRefinementFilters)
        .handleAction([resetDataTool], (state, _) => initialState.queriedRefinementFilters)
        .handleAction(changeQueriedRefinementFilters, (state, action) => action.payload),
    queriedConditionsRefinedGranularity: createReducer(
        initialState.queriedConditionsRefinedGranularity
    )
        .handleAction(
            [resetDataTool],
            (state, _) => initialState.queriedConditionsRefinedGranularity
        )
        .handleAction(changeQueriedConditionsRefinedGranularity, (state, action) => action.payload),
    queriedDataSettings: createReducer(initialState.queriedDataSettings)
        .handleAction([resetDataTool], (state, _) => initialState.queriedDataSettings)
        .handleAction(changeQueriedDataSettings, (state, action) => action.payload),

    filtersChangedSinceLastRequest: createReducer(initialState.filtersChangedSinceLastRequest)
        .handleAction(
            [dataSelectionValidation.success, resetFiltersChangedSinceLastRequest],
            (state, action) => false
        )
        .handleAction(filtersChangedSinceLastRequestActions, (state, action) => true),
    visibleRefinementFilters: createReducer(initialState.visibleRefinementFilters)
        .handleAction(
            [resetDataTool, changeSelectedDataType],
            (state, action) => initialState.visibleRefinementFilters
        )
        .handleAction(
            [initializeVisibleRefinementFilters, validateVisibleRefinementFilters],
            (state, action) => sortRefinementsOptions(action.payload)
        )
        .handleAction(addVisibleRefinementFilters, (state, action) => {
            if (state.includes(action.payload)) {
                return state;
            }
            return sortRefinementsOptions(state.concat(action.payload));
        })
        .handleAction(removeVisibleRefinementFilters, (state, action) =>
            state.filter((item) => item !== action.payload)
        ),

    preservedChartFilters: createReducer(initialState.preservedChartFilters)
        .handleAction(
            [resetDataTool, changeSelectedDataType],
            (state, _) => initialState.preservedChartFilters
        )
        .handleAction(
            [
                initializeChartFilters,
                changeChartFilters,
                validateChartFilters,
                validatePreservedChartFilters,
            ],
            (state, action) => ({
                ...state,
                ...action.payload,
            })
        ),
    chartFilters: createReducer(initialState.chartFilters)
        .handleAction([resetDataTool, changeSelectedDataType], () => initialState.chartFilters)
        .handleAction(
            [initializeChartFilters, changeChartFilters, validateChartFilters],
            (state, action) => ({
                ...state,
                ...action.payload,
            })
        )
        .handleAction([applyRelevantPreservedChartFilters], (state, action) => ({
            ...action.payload,
        }))
        .handleAction(changeChartDimension, (state, action) => omit([action.payload], state)),
    omittedChartFilters: createReducer(initialState.omittedChartFilters)
        .handleAction(
            [resetDataTool, changeSelectedChartType],
            (state, _) => initialState.omittedChartFilters
        )
        .handleAction(changeChartDimension, (state, action) => [action.payload]),
    chartDimensionFilter: createReducer(initialState.chartDimensionFilter)
        .handleAction(
            [resetDataTool, changeSelectedChartType],
            (state, _) => initialState.chartDimensionFilter
        )
        .handleAction(changeChartDimension, (state, action) => action.payload),
    isDataLoading: createReducer(initialState.isDataLoading)
        .handleAction(queryDataAsync.request, () => true)
        .handleAction([queryDataAsync.success, queryDataAsync.failure, resetDataTool], () => false),
    dataResponsesByType: createReducer(initialState.dataResponsesByType)
        .handleAction(
            [resetDataTool, queryDataAsync.request, changeSelectedDataType],
            (state, _) => initialState.dataResponsesByType
        )
        .handleAction(queryDataAsync.success, (state, action) => action.payload),
    conditionDetailNotes: createReducer(initialState.conditionDetailNotes)
        .handleAction(
            [resetDataTool, changeSelectedDataType, clearConditionDetailNotes],
            (state, _) => initialState.conditionDetailNotes
        )
        .handleAction(getConditionDetailNotes.success, (state, action) => action.payload),

    isChartDataLoading: createReducer(initialState.isChartDataLoading)
        .handleAction(
            [queryChartDataAsync.request, emulateDataIsLoadingToMakeChartsRefresh],
            () => true
        )
        .handleAction(
            [
                queryChartDataAsync.success,
                queryChartDataAsync.failure,
                resetDataTool,
                calculateChartDataResponsesFromDataQuery,
                changeSelectedDataType,
            ],
            () => false
        ),
    chartDataResponsesByType: createReducer(initialState.chartDataResponsesByType)
        .handleAction(
            [resetDataTool, changeSelectedChartType, changeSelectedDataType],
            (state, _) => initialState.chartDataResponsesByType
        )
        .handleAction(
            [queryChartDataAsync.success, calculateChartDataResponsesFromDataQuery],
            (state, action) => action.payload
        ),

    samplePopulationSize: createReducer(initialState.samplePopulationSize)
        .handleAction([resetDataTool], (state, _) => initialState.samplePopulationSize)
        .handleAction(setSamplePopulationSize, (state, action) => action.payload),
    yearRefinementFilterMode: createReducer(initialState.yearRefinementFilterMode)
        .handleAction(toggleYearRefinementFilterMode, (state, action) =>
            state === 'slider' ? 'dropdown' : 'slider'
        )
        .handleAction(setYearRefinementFilterMode, (state, action) => action.payload),
    dayRefinementFilterMode: createReducer(initialState.dayRefinementFilterMode).handleAction(
        toggleDayRefinementFilterMode,
        (state, action) => (state === 'slider' ? 'dropdown' : 'slider')
    ),
    includeNowcasts: createReducer(initialState.includeNowcasts)
        .handleAction([resetDataTool], (state, action) => initialState.includeNowcasts)
        .handleAction(initializeIncludeNowcasts, (state, action) => action.payload)
        .handleAction(toggleIncludeNowcasts, (state, action) => !state),
    activeSelectionCountModal: createReducer(initialState.activeSelectionCountModal)
        .handleAction(
            [cancelSelectionCountModal, confirmSelectionCountModal, resetDataTool],
            (state, _) => initialState.activeSelectionCountModal
        )
        .handleAction(openSelectionCountModal, (state, action) => action.payload),
    dataTableDisplayState: createReducer(initialState.dataTableDisplayState)
        .handleAction(initializeDataTableDisplayState, (state, action) => ({
            ...initialState.dataTableDisplayState,
            ...action.payload,
        }))
        .handleAction(changeSelectedDataType, (state, action) => initialState.dataTableDisplayState)
        .handleAction(resetDataTool, (state, action) => initialState.dataTableDisplayState)
        .handleAction(updateDataTableDisplayState, (state, action) => ({
            ...state,
            ...action.payload,
        })),
    customChartSettings: createReducer(initialState.customChartSettings)
        .handleAction(setCustomChartSettings, (state, action) => ({
            ...state,
            ...action.payload,
        }))
        .handleAction(
            [resetDataTool, changeSelectedDataType, changeSelectedChartType],
            (state, action) => initialState.customChartSettings
        ),
    combinedQueriedFiltersWithoutTimeUnitsCount: createReducer(
        initialState.combinedQueriedFiltersWithoutTimeUnitsCount
    )
        .handleAction(
            changeCombinedQueriedFiltersWithoutTimeUnitsCount,
            (state, action) => action.payload
        )
        .handleAction(
            [resetDataTool, changeSelectedDataType],
            (state, action) => initialState.combinedQueriedFiltersWithoutTimeUnitsCount
        ),
    singleYearAgeGranularity: createReducer(initialState.singleYearAgeGranularity)
        .handleAction(
            [resetDataTool, changeSelectedDataType, getSingleYearAgeGranularitiesAsync.request],
            (state, action) => initialState.singleYearAgeGranularity
        )
        .handleAction(
            getSingleYearAgeGranularitiesAsync.success,
            (state, action) => action.payload
        ),
    drillIntoCondition: createReducer(initialState.drillIntoCondition)
        .handleAction(setDrillIntoCondition, (state, action) => action.payload)
        .handleAction(resetDataTool, (state, action) => initialState.drillIntoCondition),
    dengueCalculations: createReducer(initialState.dengueCalculations)
        .handleAction(
            [resetDataTool, changeSelectedDataType, changeSelectedChartType],
            (state, _) => initialState.dengueCalculations
        )
        .handleAction(toggleDengueAvertedOutcome, (state) => ({
            ...state,
            showAvertedOutcome: !state.showAvertedOutcome,
            showCumulativeOutcome: !state.showAvertedOutcome ? false : state.showCumulativeOutcome,
            baselineData: !state.showAvertedOutcome ? null : state.baselineData,
        }))
        .handleAction(toggleDengueCumulativeOutcome, (state) => ({
            ...state,
            showCumulativeOutcome: !state.showCumulativeOutcome,
            showAvertedOutcome: !state.showCumulativeOutcome ? false : state.showAvertedOutcome,
            baselineData: !state.showCumulativeOutcome ? null : state.baselineData,
        }))
        .handleAction(fetchDengueBaselineDataAsync.request, (state) => ({
            ...state,
            isLoadingBaseline: true,
        }))
        .handleAction(fetchDengueBaselineDataAsync.success, (state, action) => ({
            ...state,
            baselineData: action.payload,
            isLoadingBaseline: false,
        }))
        .handleAction(fetchDengueBaselineDataAsync.failure, (state) => ({
            ...state,
            isLoadingBaseline: false,
        }))
        .handleAction(clearDengueBaselineData, (state) => ({
            ...state,
            baselineData: null,
        })),
    pdfReportSend: createReducer(initialState.pdfReportSend)
        .handleAction(sendPdfReportAsync.request, (state, action) => ({
            status: OPERATION_STATUS.PENDING,
            lastEmail: action.payload.email,
            // Keep the streak on a retry to the same address; reset on a new recipient.
            failedAttempts: action.payload.email === state.lastEmail ? state.failedAttempts : 0,
        }))
        .handleAction(sendPdfReportAsync.success, (state) => ({
            ...state,
            status: OPERATION_STATUS.SUCCEEDED,
            failedAttempts: 0,
        }))
        .handleAction(sendPdfReportAsync.failure, (state) => ({
            ...state,
            status: OPERATION_STATUS.FAILED,
            failedAttempts: state.failedAttempts + 1,
        }))
        .handleAction(clearPdfReportSendStatus, (state) => ({
            ...state,
            status: OPERATION_STATUS.IDLE,
        }))
        .handleAction([resetPdfReportSend, resetDataTool], () => initialState.pdfReportSend),
});
